import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  InputAdornment,
  Radio,
  RadioGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EditIcon from "@mui/icons-material/Edit";
import ErrorIcon from "@mui/icons-material/Error";
import InlineEdit from "../../../../../components/InlineEdit";
import FreeSearch from "../../../../../components/FreeSearch";
import { Asset } from "../../../../../types";
import {
  useCheckSim,
  useSaveESimEmail,
  useSaveSim,
} from "../../../../../hooks/assets";
import { Cancel, Done } from "@mui/icons-material";
import { makeStyles } from "@mui/styles";
import { isValidEmail } from "../../../../../helpers/functions";
import { SIMType } from "../../../../../helpers/constants";

type Props = {
  asset: Asset;
  hasAdminBar?: boolean;
};

const Component = ({ asset, hasAdminBar }: Props) => {
  const classes = useStyles();
  const initialSimDetails = {
    sim: asset.sim_serial || "",
    updated: false,
  };
  const initialESimEmailDetails = {
    eSimEmail: asset.esim_email || "",
    updated: false,
  };
  const [simType, setSimType] = useState(SIMType.SIM);
  const [simDetails, updateSimDetails] = useState(initialSimDetails);
  const [eSimEmailDetails, updateESimEmailDetails] = useState(
    initialESimEmailDetails
  );
  const handleReset = () => {
    saveSim.reset();
    saveESimEmail.reset();
    if (simType === SIMType.SIM) simValidation.refetch();
    updateSimDetails(initialSimDetails);
    updateESimEmailDetails(initialESimEmailDetails);
    setSimType(SIMType.SIM);
  };
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleReset();
    setSimType(event.target.value as SIMType);
  };
  const simValidation = useCheckSim(simDetails.sim, asset.network?.name, {
    enabled: simDetails.updated,
  });

  const [closed, setClosed] = useState(true);
  const saveSim = useSaveSim(asset, { onSuccess: () => setClosed(true) });
  const saveESimEmail = useSaveESimEmail(asset, {
    onSuccess: () => setClosed(true),
  });

  const inflightESimChange =
    asset.in_flight_mp_requests &&
    asset.in_flight_mp_requests.find(
      (r) => r.chain.purpose === "esim_sim_change"
    );

  const getSimIcon = () => {
    return (
      <Box width="24px" height="24px" display="flex" alignItems="center">
        {simValidation.isLoading ||
        saveSim.isLoading ||
        saveESimEmail.isLoading ? (
          <CircularProgress size={16} />
        ) : (saveSim.isSuccess && saveSim.data?.processed) ||
          (saveESimEmail.isSuccess && saveESimEmail.data?.processed) ? (
          <CheckCircleIcon color="success" />
        ) : !simValidation.data?.is_valid_sim_number || saveSim.isError ? (
          <Tooltip title="Invalid SIM number">
            <ErrorIcon color="error" />
          </Tooltip>
        ) : (eSimEmailDetails.eSimEmail &&
            !isValidEmail(eSimEmailDetails.eSimEmail)) ||
          saveESimEmail.isError ? (
          <Tooltip title="Invalid email">
            <ErrorIcon color="error" />
          </Tooltip>
        ) : (
          <EditIcon />
        )}
      </Box>
    );
  };

  return (
    <div className={classes.root}>
      <InlineEdit
        onEdit={handleReset}
        open={!closed}
        disabled={
          hasAdminBar ||
          asset.status === "Terminated" ||
          asset.status === "Disconnected"
        }
        buttonLabel="SIM Swap"
        className={classes.edit}
        input={
          <div>
            <RadioGroup row value={simType} onChange={handleChange}>
              <FormControlLabel
                value={SIMType.SIM}
                control={<Radio />}
                label="Physical SIM"
              />
              <FormControlLabel
                value={SIMType.ESIM}
                control={<Radio />}
                label="eSIM"
              />
            </RadioGroup>
            {simType === SIMType.SIM && (
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  saveSim.mutate(simDetails.sim);
                }}
              >
                <FreeSearch
                  afterTyping={(value) => {
                    saveSim.reset();
                    updateSimDetails({
                      sim: value,
                      updated: asset.sim_serial !== value,
                    });
                  }}
                  disabled={saveSim.isLoading}
                  helperText={
                    "To perform a SIM swap, simply enter the new SIM ref and click Save Changes."
                  }
                  inputProps={{
                    style: { minWidth: 120 },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end" className={classes.icons}>
                        {getSimIcon()}
                      </InputAdornment>
                    ),
                  }}
                  name="sim_serial"
                  value={simDetails.sim}
                  size="small"
                  variant="outlined"
                  wait={200}
                  numbersOnly
                  fullWidth
                />
                <div className={classes.buttons}>
                  <Button
                    color="primary"
                    variant="contained"
                    startIcon={<Done />}
                    disabled={
                      saveSim.isLoading ||
                      !simValidation.data?.is_valid_sim_number ||
                      !simDetails.updated ||
                      !simDetails.sim
                    }
                    type={
                      !!simValidation.data?.is_valid_sim_number
                        ? "submit"
                        : "button"
                    }
                  >
                    Save Changes
                  </Button>
                  <Button
                    color="primary"
                    variant="contained"
                    startIcon={<Cancel />}
                    disabled={saveSim.isLoading || !simDetails.updated}
                    className={classes.discard}
                    onClick={() => {
                      saveSim.reset();
                      updateSimDetails(initialSimDetails);
                    }}
                  >
                    Discard Changes
                  </Button>
                </div>
              </form>
            )}
            {simType === SIMType.ESIM && inflightESimChange && (
              <Typography variant="caption">
                You cannot request a new eSIM at this time as there is an
                existing inflight eSIM request for this mobile. Please check
                back later.
              </Typography>
            )}
            {simType === SIMType.ESIM && !inflightESimChange && (
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  saveESimEmail.mutate(eSimEmailDetails.eSimEmail);
                }}
              >
                <FreeSearch
                  afterTyping={(value) => {
                    saveESimEmail.reset();
                    updateESimEmailDetails({
                      eSimEmail: value,
                      updated: asset.esim_email !== value,
                    });
                  }}
                  disabled={saveESimEmail.isLoading}
                  helperText={
                    "To perform a SIM swap, simply enter the recipient's email and click Save Changes."
                  }
                  inputProps={{
                    style: { minWidth: 120 },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end" className={classes.icons}>
                        {getSimIcon()}
                      </InputAdornment>
                    ),
                  }}
                  name="esim_email"
                  value={eSimEmailDetails.eSimEmail}
                  size="small"
                  variant="outlined"
                  wait={200}
                  fullWidth
                />
                <div className={classes.buttons}>
                  <Button
                    color="primary"
                    variant="contained"
                    startIcon={<Done />}
                    disabled={
                      saveESimEmail.isLoading ||
                      !eSimEmailDetails.updated ||
                      !eSimEmailDetails.eSimEmail ||
                      !isValidEmail(eSimEmailDetails.eSimEmail)
                    }
                    type={
                      isValidEmail(eSimEmailDetails.eSimEmail)
                        ? "submit"
                        : "button"
                    }
                  >
                    Save Changes
                  </Button>
                  <Button
                    color="primary"
                    variant="contained"
                    startIcon={<Cancel />}
                    disabled={
                      saveESimEmail.isLoading || !eSimEmailDetails.updated
                    }
                    className={classes.discard}
                    onClick={() => {
                      saveESimEmail.reset();
                      updateESimEmailDetails(initialESimEmailDetails);
                    }}
                  >
                    Discard Changes
                  </Button>
                </div>
              </form>
            )}
          </div>
        }
      >
        <Typography variant="h4">
          {asset.esim_email || asset.sim_serial}
        </Typography>
        {saveESimEmail.isSuccess &&
          saveESimEmail.data?.status === "success" && (
            <Typography variant="caption">
              Your QR code will be provided by Email shortly.
            </Typography>
          )}
        {saveSim.isSuccess && saveSim.data?.status === "success" && (
          <Typography variant="caption">
            Please note any network changes may take up to 24 hours to be
            applied.
          </Typography>
        )}
      </InlineEdit>
      {saveSim.isError ||
        (saveESimEmail.isError && (
          <Typography variant="caption" color="error">
            The action you have taken has failed to complete at the network,
            please try again. If the problem continues please contact the Giacom
            Support Team.
          </Typography>
        ))}
    </div>
  );
};

const useStyles = makeStyles(() => ({
  root: { maxWidth: 400 },
  edit: { flexGrow: 0, paddingRight: 16 },
  search: { width: "100%" },
  icons: { paddingLeft: 14 },
  discard: { marginLeft: 14 },
  buttons: {
    display: "flex",
    justifyContent: "space-between",
    paddingTop: 10,
    paddingBottom: 5,
  },
}));

export default Component;
